import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { Col, FormControl, FormGroup, Row } from 'react-bootstrap';
import { useCombobox } from 'downshift';
import classNames from 'classnames';

const ownerPropTypes = PropTypes.shape({
  id: PropTypes.number.isRequired,
  fullName: PropTypes.string.isRequired,
});

ownerPropTypes.children = PropTypes.arrayOf(ownerPropTypes);

const OwnersSelect = (props) => {
  const { handleSelect } = props;
  return (
    <FormGroup bsClass="m-0">
      <OwnersCombobox {...props} handleSelect={handleSelect} />
    </FormGroup>
  );
};

const OwnersCombobox = ({
  projectOwners,
  selectedOwnerIds,
  handleSelect,
  disabled,
}) => {
  const [inputItems, setInputItems] = useState([]);
  const isActive = (id) => selectedOwnerIds.includes(id);
  const isDisabled = (selectedItem) =>
    !isActive(selectedItem.id) && selectedItem.archived;
  useEffect(() => {
    setInputItems(projectOwners);
  }, [projectOwners]);

  const {
    getComboboxProps,
    getInputProps,
    getItemProps,
    getMenuProps,
    getToggleButtonProps,
    isOpen,
    highlightedIndex,
  } = useCombobox({
    items: inputItems,
    itemToString: (item) => (item ? item.name : ''),
    onInputValueChange: ({ inputValue }) => {
      if (inputValue) {
        setInputItems(
          projectOwners.filter(({ fullName }) =>
            fullName.toLowerCase().includes(inputValue.toLowerCase())
          )
        );
      } else {
        setInputItems([...projectOwners]);
      }
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (!isDisabled(selectedItem)) {
        handleSelect(selectedItem.id);
      }
    },
    selectedItem: null,
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: true,
            highlightedIndex: state.highlightedIndex,
            inputValue: '', // Don't add the item string as input value at selection
          };
        case useCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            inputValue: '',
          };
        default:
          return changes;
      }
    },
  });

  return (
    <Row>
      <Col xs={12}>
        <div
          className={classNames('dropdown', { open: isOpen })}
          style={disabled ? { background: '#eeeeee' } : {}}
        >
          <div className="input-group" {...getComboboxProps()}>
            <FormControl
              className="clear"
              placeholder="Start Typing"
              disabled={disabled}
              {...getInputProps({ id: 'projectOwners', refKey: 'inputRef' })}
            />
            <div className="input-group-btn">
              <div
                className="dropdown-toggle btn btn-info btn-clear btn-no-left-border"
                style={
                  disabled
                    ? { background: '#eeeeee', padding: '15px' }
                    : { padding: '15px' }
                }
                role="button"
                {...getToggleButtonProps()}
              >
                <span className="caret" />
              </div>
            </div>
          </div>
          <ul
            className="dropdown-menu"
            {...getMenuProps()}
            style={disabled ? { pointerEvents: 'none' } : {}}
          >
            {inputItems.map((owner, index) => {
              const { id, archived } = owner;
              const active = isActive(id);
              const disabled = !active && archived;
              return (
                <li
                  key={owner.id}
                  className={classNames({ active, disabled })}
                  {...getItemProps({
                    index,
                    tabIndex: index,
                  })}
                >
                  <a>
                    {owner.archived
                      ? `${owner.fullName} (archived)`
                      : owner.fullName}
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      </Col>
    </Row>
  );
};

export default OwnersSelect;

OwnersSelect.propTypes = {
  projectOwners: PropTypes.arrayOf(ownerPropTypes),
  onShowEmployeeModal: PropTypes.func,
};
